import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ProgressMonitor from '../components/ProgressMonitor';
import ContentEditorModal from '../components/ContentEditorModal';
import FactsAgentPromptsEditor from '../components/FactsAgentPromptsEditor';
import api, { factsAgentAPI, handleAPIError } from '../services/api';
import type { JobStatus, FactsAgentResults, Job } from '../types';

type PageState = 'upload' | 'processing' | 'completed' | 'error';
type PageView = 'generator' | 'prompts';

export default function FactsAgentPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/factsagent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  // Main view state
  const [activeView, setActiveView] = useState<PageView>('generator');

  const [pageState, setPageState] = useState<PageState>('upload');
  const [jobId, setJobId] = useState<number | null>(null);
  const [results, setResults] = useState<FactsAgentResults | null>(null);
  const [error, setError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [textContent, setTextContent] = useState<string>('');
  const [facilityAddress, setFacilityAddress] = useState<string>('');

  // Recent jobs state
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [, setLoadingJobs] = useState(false);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<{ stepNumber: number; stepName: string } | null>(null);

  const urlJobId = searchParams.get('job_id');

  // Load recent jobs when component mounts
  useEffect(() => {
    if (projectId) {
      loadRecentJobs();
    }
  }, [projectId]);

  // Auto-load specific job from URL
  useEffect(() => {
    if (urlJobId && projectId) {
      const id = parseInt(urlJobId);
      if (!isNaN(id)) {
        viewJob({ id } as Job);
      }
    }
  }, [urlJobId, projectId]);

  const loadRecentJobs = async () => {
    if (!projectId) return;

    try {
      setLoadingJobs(true);
      // Remove limit to show ALL completed fact sheets, not just recent ones
      const response = await api.get(`/projects/${projectId}/jobs?system_type=factsagent&status=completed`);
      setRecentJobs(response.data);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const viewJob = async (job: Job) => {
    try {
      setJobId(job.id);
      const resultsData = await factsAgentAPI.getResults(job.id);
      setResults(resultsData);
      setPageState('completed');
    } catch (err) {
      setError(handleAPIError(err));
      setPageState('error');
    }
  };

  const handleSubmit = async () => {
    if (!projectId) {
      setError('No project ID provided. Please select a project first.');
      setPageState('error');
      return;
    }

    if (!textContent.trim()) {
      setError('Please enter transcript text.');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const uploadResponse = await factsAgentAPI.uploadTranscriptText(textContent, parseInt(projectId), facilityAddress.trim() || undefined);
      const jobIdMatch = uploadResponse.message.match(/Job ID: (\d+)/);
      if (!jobIdMatch) {
        throw new Error('Failed to get job ID from upload response');
      }

      const newJobId = parseInt(jobIdMatch[1]);
      setJobId(newJobId);
      await factsAgentAPI.startProcessing(newJobId);
      setPageState('processing');
    } catch (err) {
      setError(handleAPIError(err));
      setPageState('error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleStatusUpdate = async (status: JobStatus) => {
    if (status.status === 'completed') {
      if (jobId) {
        try {
          const resultsData = await factsAgentAPI.getResults(jobId);
          setResults(resultsData);
          setPageState('completed');
        } catch (err) {
          setError(handleAPIError(err));
          setPageState('error');
        }
      }
    } else if (status.status === 'failed') {
      setError(status.error_message || 'Job processing failed');
      setPageState('error');
    }
  };

  const handleReset = () => {
    setPageState('upload');
    setJobId(null);
    setResults(null);
    setError('');
    loadRecentJobs();
  };

  const handleDelete = async () => {
    if (!jobId) return;

    if (!confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await factsAgentAPI.deleteJob(jobId);
      handleReset();
      loadRecentJobs();
    } catch (err) {
      alert('Failed to delete job: ' + handleAPIError(err));
    }
  };

  const openEditor = (stepNumber: number, stepName: string) => {
    setEditingStep({ stepNumber, stepName });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingStep(null);
  };

  const handleEditorSave = async () => {
    if (jobId) {
      try {
        const resultsData = await factsAgentAPI.getResults(jobId);
        setResults(resultsData);
      } catch (err) {
        console.error('Failed to refresh results:', err);
      }
    }
  };

  return (
    <Layout projectId={projectId ? parseInt(projectId) : null}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">FactsAgent</h1>
              <p className="page-subtitle">
                Upload a raw transcript to extract verified client facts used as the source of truth for content generation
                <a href="https://drive.google.com/file/d/13Z8_3JLES4RziICQNWZQGWEI6CpdyIZQ/view" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
              </p>
            </div>
            {pageState === 'completed' && activeView === 'generator' && (
              <div className="flex items-center gap-3">
                <a
                  href={results ? factsAgentAPI.downloadAll(results.job.id) : '#'}
                  download
                  className="btn-primary"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download All
                </a>
                <button onClick={handleReset} className="btn-secondary">
                  New Job
                </button>
              </div>
            )}
          </div>

          {/* View Tabs */}
          <div className="mt-4 border-b border-gray-200">
            <div className="flex gap-6">
              <button
                onClick={() => setActiveView('generator')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeView === 'generator'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generator
                </div>
              </button>
              <button
                onClick={() => setActiveView('prompts')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeView === 'prompts'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Prompt Templates
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Prompts View */}
        {activeView === 'prompts' && (
          <div className="mt-6">
            <FactsAgentPromptsEditor
              projectId={projectId ? parseInt(projectId) : null}
            />
          </div>
        )}

        {/* Generator View */}
        {activeView === 'generator' && (
          <>
        {/* All Fact Sheets Section */}
        {recentJobs.length > 0 && pageState === 'upload' && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">All Fact Sheets ({recentJobs.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentJobs.map((job) => {
                const title = job.settings?.filename
                  ? job.settings.filename.replace(/\.(txt|json)$/i, '')
                  : `Fact Sheet ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                return (
                  <div
                    key={job.id}
                    onClick={() => viewJob(job)}
                    className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group"
                  >
                    <div className="card-body">
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-success-400 to-success-600 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors truncate" title={title}>
                            {title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {new Date(job.completed_at || job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                          {(job.settings as any)?.edited_at && (job.settings as any)?.edited_by_name && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              <span>Edited by {(job.settings as any).edited_by_name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className="text-brand-600 text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                          View/Edit
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">Or create a new fact sheet below</p>
            </div>
          </div>
        )}

        {/* Upload State */}
        {pageState === 'upload' && (
          <div className="max-w-2xl mx-auto">
            <div className="card">
              <div className="card-body">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Enter Transcript</h2>
                <p className="text-gray-500 mb-6">
                  Paste or type your interview transcript to begin processing through the 6-step FactsAgent pipeline.
                </p>

                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Paste your interview transcript here..."
                  className="textarea h-64 font-mono text-sm"
                  disabled={isUploading}
                />

                <div className="mt-4">
                  <label className="label">
                    Facility Address <span className="text-gray-400 font-normal">(recommended)</span>
                  </label>
                  <input
                    type="text"
                    value={facilityAddress}
                    onChange={(e) => setFacilityAddress(e.target.value)}
                    placeholder="e.g. 7349 Statesville Rd, Charlotte, NC 28269"
                    className="input font-mono text-sm"
                    disabled={isUploading}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Format: Street, City, ST ZIP — This address will be appended to the facts and used by Content Framework for page generation.
                  </p>
                </div>

                {error && (
                  <div className="mt-3 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={isUploading || !textContent.trim()}
                  className="btn-primary w-full mt-4"
                >
                  {isUploading ? (
                    <>
                      <span className="spinner"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Start Processing
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info Card */}
            <div className="mt-6 card bg-accent-50 border-accent-200">
              <div className="card-body">
                <h3 className="text-lg font-semibold text-accent-900 mb-2">What happens next?</h3>
                <p className="text-accent-700 mb-3">Your transcript will go through 6 processing steps:</p>
                <ol className="list-decimal list-inside space-y-1.5 text-sm text-accent-700">
                  <li>Clean Transcript - Remove formatting and normalize text</li>
                  <li>Format Transcript - Structure the content</li>
                  <li>Extract Facts & Policies - Extract key information and policy details</li>
                  <li>Verify Completeness - Check for missing data</li>
                  <li>Check Duplicates - Remove redundant information</li>
                  <li>Consolidate Final Facts - Merge all facts into final output</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* Processing State */}
        {pageState === 'processing' && jobId && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Processing Job #{jobId}
                  </h2>
                  <p className="text-gray-500">
                    Your transcript is being processed. This may take several minutes.
                  </p>
                </div>
                <ProgressMonitor
                  jobId={jobId}
                  onStatusUpdate={handleStatusUpdate}
                />
              </div>
            </div>
          </div>
        )}

        {/* Completed State */}
        {pageState === 'completed' && results && (
          <div className="max-w-4xl mx-auto">
            <div className="card mb-6">
              <div className="card-header bg-success-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-success-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-success-900">Processing Complete</h2>
                    <p className="text-sm text-success-700">All 6 steps completed successfully</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {results.outputs
                .sort((a, b) => a.step_number - b.step_number)
                .map((output) => (
                  <div
                    key={output.id}
                    className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group"
                    onClick={() => openEditor(output.step_number, output.step_name)}
                  >
                    <div className="card-body">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center">
                            <span className="text-white font-bold">{output.step_number}</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors">
                              {output.step_name}
                            </h3>
                            {output.word_count && (
                              <p className="text-sm text-gray-500 mt-1">
                                {output.word_count.toLocaleString()} words
                              </p>
                            )}
                            {output.edited_at && output.edited_by_name && (
                              <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>Edited by {output.edited_by_name}</span>
                              </div>
                            )}
                            <span className="text-brand-600 text-sm font-medium mt-2 inline-flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              View/Edit
                            </span>
                          </div>
                        </div>
                        <a
                          href={factsAgentAPI.downloadStep(results.job.id, output.step_number)}
                          download
                          onClick={(e) => e.stopPropagation()}
                          className="btn-ghost btn-sm"
                          title="Download"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Job Management */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Job Management</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Job ID: {results.job.id} • Completed: {results.job.completed_at ? new Date(results.job.completed_at).toLocaleString() : new Date(results.job.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button onClick={handleDelete} className="btn-danger">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Job
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {pageState === 'error' && (
          <div className="max-w-2xl mx-auto">
            <div className="card border-danger-200 bg-danger-50">
              <div className="card-body text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-danger-100 flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-danger-900 mb-2">Error</h2>
                <p className="text-danger-700 mb-4">{error}</p>
                <button onClick={handleReset} className="btn-primary">
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Content Editor Modal */}
      {editorOpen && editingStep && jobId && (
        <ContentEditorModal
          jobId={jobId}
          stepNumber={editingStep.stepNumber}
          stepName={editingStep.stepName}
          isOpen={editorOpen}
          onClose={closeEditor}
          onSave={handleEditorSave}
        />
      )}
    </Layout>
  );
}
